<template>
  <div class="Field-Text">
    <FieldHeader
      v-if="isHeaderShowed && !props.isCounterBottom"
      class="Field-Text__hdr"
      :label="label"
      :option="option"
      :maxItems="maxSybmols"
      :counter="counter"
    />

    <template v-if="isInput">
      <input
        class="Field-Text__input"
        :class="get_modifyClasses"
        :name="name"
        :placeholder="placeholder"
        :value="modelValue"
        :required="props.required"
        @input="onInputChange"
        @focus="toFocus"
        @blur="toBlur"
        type="text"
        ref="input"
      />
    </template>

    <template v-if="isTextarea">
      <textarea
        class="Field-Text__input"
        :class="get_modifyClasses"
        :name="name"
        :placeholder="placeholder"
        :value="modelValue"
        :required="props.required"
        @focus="toFocus"
        @blur="toBlur"
        @input="onTextareaChange"
        ref="input"
      />
    </template>

    <!-- Ошибка -->
    <p v-if="get_isError" class="Field-Text__error-text">{{ errorMessage }}</p>

    <!-- Счётчик снизу -->
    <div v-if="props.isCounterBottom && props.maxSybmols" class="Field-Text__counter">
      {{ counter }} / {{ props.maxSybmols }}
    </div>
  </div>
</template>

<script setup>
  import { defineProps, defineEmits, computed, ref, onMounted, nextTick } from "vue";
  import { Props } from "./scripts/props";
  import { useStore } from "vuex";
  //---------------------------------
  import FieldHeader from "@shared/UI/Fields/FieldHeader/FieldHeader.vue";
  //---------------------------------
  const store = useStore();
  const props = defineProps(Props());
  const emit = defineEmits(['update:modelValue', 'update-value', 'toFocus', 'toBlur']);
  //---------------------------------
  const counter = ref(0);
  const input = ref(null);
  //---------------------------------
  //Это текстовый input
  const isInput = computed(() => props.type === 'input');
  //Это textarea
  const isTextarea = computed(() => props.type === 'textarea');
  //Отображать ли шапку для поля (если не снизу)
  const isHeaderShowed = computed(() => props.label || props.option || props.maxSybmols);
  //Отображать ли текст ошибки
  const get_isError = computed(() => props.errorMessage && props.errorMessage !== '');
  //Определить классы модификаторы для поля
  const get_modifyClasses = computed(() => `
    ${ (get_isError.value) ? 'Field-Text__input--error' : '' }
    ${ (props.disabled) ? 'Field-Text__input--disabled' : '' }
  `)
  //---------------------------------
  // Methods

  const applyMaxSymbols = (value) => {
    if (props.maxSybmols && value.length > props.maxSybmols) {
      return value.slice(0, props.maxSybmols);
    }
    return value;
  };

  const onInputChange = (event) => {
    let value = event.target.value;
    if (props.onlyDigital) {
      value = processDigitalInput(value);
    }
    value = applyMaxSymbols(value);
    event.target.value = value;

    updateCounter(value);
    emit('update:modelValue', value);
    emit('update-value');
  };

  const onTextareaChange = (event) => {
    let value = event.target.value;
    if (props.onlyDigital) {
      value = processDigitalInput(value);
    }
    value = applyMaxSymbols(value);
    event.target.value = value;

    updateCounter(value);
    nextTick(() => {
      if (input.value) {
        adjustTextareaHeight();
      }
    });
    emit('update:modelValue', value);
  };

  const processDigitalInput = (value) => {
    value = value.replace(/[^0-9,.]/g, '');
    value = value.replace(',', '.');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts.shift() + '.' + parts.join('');
    }
    if (parts[1]) {
      parts[1] = parts[1].slice(0, 2);
      value = parts.join('.');
    }
    return value;
  };

  const updateCounter = (text) => {
    counter.value = text?.length || 0;
  };

  const adjustTextareaHeight = () => {
    const textarea = input.value;
    if (textarea) {
      textarea.style.maxHeight = '';
      const currentFieldSize = (textarea.scrollHeight < 90) ? 90 : textarea.scrollHeight;
      textarea.style.maxHeight = `${currentFieldSize}px`;
      textarea.style.height = `${currentFieldSize}px`;
    }
  };

  //Фокус на поле
  const toFocus = () => {
    store.commit('FormsInterfaceSystem/toToggleFocusFormTextElement', true);
  }
  //Потеря фокуса на поле
  const toBlur = () => {
    store.commit('FormsInterfaceSystem/toToggleFocusFormTextElement', false);
  }
  //---------------------------------
  onMounted(async () => {
    if(isTextarea.value) {
      emit('update:modelValue', props.initValue);
      updateCounter(props.initValue);
      await nextTick();
      adjustTextareaHeight();
    }
  });
</script>

<style scoped lang="scss">
  @import "styles/style";
</style>
